import type { Subscription, LevelPlanes, LevelPadding, ResolvedLevelBounds, LevelZRange } from '@pointorama/potree-shared';
import { ReactiveProperty, hasGlobalPropertyService, getGlobalPropertyService, PropertyType, ensureFloorIsolationPropertyRegistered, getFloorIsolationProperty, registerFloorProperties, isMeaningfulCeiling, resolveLevelBounds, levelZRange } from '@pointorama/potree-shared';
import { DetectedFloorPlane } from '../floorDetection';
import { FloorIsolationStrategy, IsolationSpec, resolveStrategyToClipFragment, defaultIsolationSpec, boundsFromPlanes, loadFloorFootprintCorners } from './FloorIsolationController';
import type { Viewer } from '../../viewer/viewer';
import type { RenderView } from '../../viewer/RenderView';
import type { IsolationClipFragment } from '../../viewer/buildClipState';

const FLOOR_PLANE_POSITION = 'floor_plane_position';
const FLOOR_PLANE_NORMAL = 'floor_plane_normal';
const CEILING_PLANE_POSITION = 'ceiling_plane_position';
const CEILING_PLANE_NORMAL = 'ceiling_plane_normal';

interface Coord {
  x: number;
  y: number;
  z: number;
}

interface ObjectsChangedEvent {
  floorGroupsData?: { groupIdentifier: string }[];
}

function isValidCoord(val: unknown): val is Coord {
  if (!val || typeof val !== 'object') return false;
  const c = val as Partial<Coord>;
  return typeof c.x === 'number' && typeof c.y === 'number' && typeof c.z === 'number';
}

function buildPlane(position: Coord, normal: Coord): DetectedFloorPlane {
  const boundsSize = 10;
  return {
    height: position.z,
    center: { ...position } as DetectedFloorPlane['center'],
    normal: { ...normal } as DetectedFloorPlane['normal'],
    pointCount: 0,
    confidence: 1.0,
    bounds: {
      min: { x: position.x - boundsSize / 2, y: position.y - boundsSize / 2, z: position.z - 0.1 },
      max: { x: position.x + boundsSize / 2, y: position.y + boundsSize / 2, z: position.z + 0.1 },
    } as DetectedFloorPlane['bounds'],
  };
}

/**
 * Read the current floor/ceiling plane snapshot for a floor group straight
 * from the reactive plane properties. Returns null while the property
 * service isn't available or the floor plane isn't loaded yet.
 */
export function fetchFloorPlanes(floorGroupId: string): { floor: DetectedFloorPlane; ceiling: DetectedFloorPlane | null } | null {
  if (!hasGlobalPropertyService()) return null;
  const service = getGlobalPropertyService();
  registerFloorProperties();

  const floorPos = service.getValue(FLOOR_PLANE_POSITION, floorGroupId, PropertyType.PROJECT_GLOBAL);
  const floorNorm = service.getValue(FLOOR_PLANE_NORMAL, floorGroupId, PropertyType.PROJECT_GLOBAL);
  if (!isValidCoord(floorPos) || !isValidCoord(floorNorm)) return null;

  const floor = buildPlane(floorPos, floorNorm);

  const ceilingPos = service.getValue(CEILING_PLANE_POSITION, floorGroupId, PropertyType.PROJECT_GLOBAL);
  const ceilingNorm = service.getValue(CEILING_PLANE_NORMAL, floorGroupId, PropertyType.PROJECT_GLOBAL);
  // The ceiling property defaults to {0,0,0} when never set — only a ceiling
  // meaningfully above the floor counts as real.
  const ceiling = isValidCoord(ceilingPos) && isValidCoord(ceilingNorm) && isMeaningfulCeiling(floorPos.z, ceilingPos.z) ? buildPlane(ceilingPos, ceilingNorm) : null;

  return { floor, ceiling };
}

/**
 * Subscribe to the four reactive plane properties (floor/ceiling position
 * and normal) of a floor group, so dimension edits propagate without a
 * snapshot round-trip. Fires on future changes only. Returns the
 * subscriptions (empty when the property service isn't available yet).
 */
export function subscribeToFloorPlaneProperties(floorGroupId: string, listener: () => void): Subscription[] {
  if (!hasGlobalPropertyService()) return [];
  const service = getGlobalPropertyService();
  registerFloorProperties();
  return [FLOOR_PLANE_POSITION, FLOOR_PLANE_NORMAL, CEILING_PLANE_POSITION, CEILING_PLANE_NORMAL].map((identifier) => service.getProperty(identifier, floorGroupId, PropertyType.PROJECT_GLOBAL).subscribeToChanges(listener));
}

/**
 * Coordinates floor isolation across ALL render views. Isolation is per-view:
 * the manager holds an `IsolationSpec` (a base strategy + optional per-view-id
 * overrides), resolves a clip fragment for EACH RenderView based on that view's
 * current view-mode id, and assigns it to the view's `floorIsolation` slot. The
 * render loop composes each view's fragment into its own clip; the main view's
 * fragment is also injected into the persistent material state (for picking).
 *
 * Two mutually exclusive modes:
 *
 * - **Property-driven** (default). The reactive property `floor-isolation.<id>`
 *   reflects the user's UI toggle. Subscriptions activate/deactivate with
 *   `defaultIsolationSpec`. Mutual exclusion: enabling floor B writes false to
 *   floor A's property.
 *
 * - **Custom mode**. Personas (Floorplan, Surface) call `enterCustomMode` /
 *   `exitCustomMode`; tools push specs via `setCustomSpec` (or the single-strategy
 *   `setCustomStrategy`). While custom mode is active the toggle UI is hidden and
 *   the reactive property is neither read nor written.
 *
 * Re-resolution is driven by: spec/floor changes, main-view `view_changed`, and
 * each widget's `currentViewId`. `freeze`/`unfreeze` drops/restores all clips.
 */
export class FloorIsolationManager {
  private readonly viewer: Viewer;
  private activeSpec: IsolationSpec | null = null;
  private activeFloorGroupId: string | null = null;
  private readonly toggleSubscriptions = new Map<string, Subscription>();
  /** Views the manager has attached a re-resolve subscription to. */
  private readonly trackedViews = new Set<RenderView>();
  /** Per-widget `currentViewId` subscriptions (main re-resolves via `view_changed`). */
  private readonly viewSubscriptions = new Map<RenderView, Subscription>();
  private viewChangedUnsub: (() => void) | null = null;
  private customMode = false;
  /** All known floor-group ids, rebuilt from every `objects_changed` (which carries the full list). */
  private readonly knownFloorGroupIds = new Set<string>();
  /**
   * Freeze count — while > 0, every view's isolation clip is dropped. Used by
   * tools that install their own view-scoped clip (e.g. door/window wall focus)
   * where the floor slab would leak. Refcounted; `unfreeze` restores the stored
   * spec automatically.
   */
  private frozenRefCount = 0;

  /** Whether the FloorIsolationToggle UI should render. */
  readonly uiVisible$ = new ReactiveProperty<boolean>(true);

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.viewer.scene.addEventListener('objects_changed', this.handleObjectsChanged);
    this.viewer.scene.addEventListener('objects_deleted', this.handleObjectsDeleted);
  }

  getActiveFloorGroupId(): string | null {
    return this.activeFloorGroupId;
  }

  /** Current per-view spec, or null when inactive. Read by the debug widget. */
  getActiveSpec(): IsolationSpec | null {
    return this.activeSpec;
  }

  /**
   * Z-range of the MAIN view's isolation slab (plane strategies only), or null.
   * Used by room segmentation to filter the captured depth map by height.
   */
  getMainViewSlabZRange(): { z: number; tolerance: number } | null {
    const plane = this.viewer.renderViews[0]?.floorIsolation?.clipPlanes[0];
    if (!plane) return null;
    return { z: plane.position.z, tolerance: (plane.plane as unknown as { tolerance: number }).tolerance };
  }

  isActive(floorGroupId: string): boolean {
    return this.activeFloorGroupId === floorGroupId;
  }

  // ─── Level-bounds snapshots (potree-side LevelBounds entry point) ─────

  /**
   * Snapshot of every known floor group's plane heights, sorted by floorZ.
   * Groups whose floor plane isn't loaded yet are skipped.
   */
  getLevelPlanes(): LevelPlanes[] {
    const levels: LevelPlanes[] = [];
    for (const groupIdentifier of this.knownFloorGroupIds) {
      const planes = fetchFloorPlanes(groupIdentifier);
      if (!planes) continue;
      levels.push({ groupIdentifier, floorZ: planes.floor.height, ceilingZ: planes.ceiling?.height ?? null });
    }
    return levels.sort((a, b) => a.floorZ - b.floorZ);
  }

  /** Resolve a floor group's level bounds against all known floor groups. */
  resolveLevelBounds(floorGroupId: string): ResolvedLevelBounds | null {
    return resolveLevelBounds(this.getLevelPlanes(), floorGroupId);
  }

  /** Resolve a floor group's z-range with an explicit padding request. */
  resolveLevelZRange(floorGroupId: string, padding: LevelPadding = 'none'): LevelZRange | null {
    const bounds = this.resolveLevelBounds(floorGroupId);
    return bounds ? levelZRange(bounds, padding) : null;
  }

  isInCustomMode(): boolean {
    return this.customMode;
  }

  isFrozen(): boolean {
    return this.frozenRefCount > 0;
  }

  /**
   * Register a RenderView so its isolation re-resolves when its mode changes.
   * Called by `ensureMainView`/`ensureWidgetView` on view creation. Main views
   * re-resolve via the global `view_changed`; widgets via their `currentViewId`.
   * Idempotent. Resolves the view immediately so it picks up the active spec.
   */
  attachView(view: RenderView): void {
    if (this.trackedViews.has(view)) return;
    this.trackedViews.add(view);

    if (view === this.viewer.renderViews[0]) {
      this.ensureViewChangedSubscription();
    } else if (view.viewState) {
      // `subscribe` fires immediately, handling the initial seed.
      const sub = view.viewState.currentViewId.subscribe(() => this.resolveView(view));
      this.viewSubscriptions.set(view, sub);
    }
    this.resolveView(view);
  }

  private ensureViewChangedSubscription(): void {
    if (this.viewChangedUnsub) return;
    const vm = this.viewer.personaManager?.viewManager;
    if (!vm) return;
    const handler = (): void => {
      const main = this.viewer.renderViews[0];
      if (main) this.resolveView(main);
    };
    vm.addEventListener('view_changed', handler);
    this.viewChangedUnsub = () => vm.removeEventListener('view_changed', handler);
  }

  /**
   * Enter custom mode. Hides the toggle UI and suspends the property-driven
   * path. Tools push their own spec via `setCustomSpec` shortly after.
   */
  enterCustomMode(): void {
    if (this.customMode) return;
    this.customMode = true;
    this.uiVisible$.value = false;
    this.activeSpec = null;
    this.activeFloorGroupId = null;
    this.resolveAllViews();
  }

  /**
   * Exit custom mode. Restores the toggle UI and re-evaluates persisted toggle
   * properties: any floor whose toggle is true is re-activated.
   */
  exitCustomMode(): void {
    if (!this.customMode) return;
    this.activeSpec = null;
    this.activeFloorGroupId = null;
    this.customMode = false;
    this.uiVisible$.value = true;

    for (const floorGroupId of this.toggleSubscriptions.keys()) {
      const property = getFloorIsolationProperty(floorGroupId);
      if (property?.value === true && this.activate(floorGroupId)) return;
    }
    this.resolveAllViews();
  }

  /** Acquire a freeze on isolation. While frozen, every view's clip is dropped. */
  freeze(): void {
    this.frozenRefCount++;
    if (this.frozenRefCount === 1) this.resolveAllViews();
  }

  unfreeze(): void {
    if (this.frozenRefCount === 0) return;
    this.frozenRefCount--;
    if (this.frozenRefCount === 0) this.resolveAllViews();
  }

  /** Owners currently holding a freeze via acquire(). */
  private readonly freezeOwners = new Set<string>();

  /**
   * Owner-keyed freeze handle — prefer over bare freeze()/unfreeze() pairs:
   * release is idempotent (a double or missed release can't corrupt the
   * refcount) and a leak is attributable to its owner. Re-acquiring under
   * the same owner shares the existing hold.
   */
  acquire(owner: string): () => void {
    if (!this.freezeOwners.has(owner)) {
      this.freezeOwners.add(owner);
      this.freeze();
    }
    let released = false;
    return (): void => {
      if (released) return;
      released = true;
      if (this.freezeOwners.delete(owner)) {
        this.unfreeze();
      }
    };
  }

  /**
   * Apply (or replace) a tool-driven per-view spec. No-op outside custom mode.
   * `floorGroupId` is used for mutual exclusion / segmentation bookkeeping.
   */
  setCustomSpec(spec: IsolationSpec, floorGroupId: string | null): void {
    if (!this.customMode) return;
    this.activeSpec = spec;
    this.activeFloorGroupId = floorGroupId;
    this.resolveAllViews();
  }

  /** Back-compat: apply a single strategy to all views. */
  setCustomStrategy(strategy: FloorIsolationStrategy): void {
    const floorGroupId = strategy.kind === 'box' ? null : strategy.floorGroupId;
    this.setCustomSpec({ base: strategy }, floorGroupId);
  }

  /** Drop the tool-driven clip without leaving custom mode. */
  clearCustomStrategy(): void {
    if (!this.customMode) return;
    this.activeSpec = null;
    this.activeFloorGroupId = null;
    this.resolveAllViews();
  }

  /**
   * Property-driven activation. Sets `defaultIsolationSpec` for the floor and
   * resolves all views. Mutual exclusion flips the previous floor's toggle off.
   * Returns false (leaving the toggle untouched) when floor coords aren't ready.
   */
  activate(floorGroupId: string): boolean {
    if (this.customMode) return false;

    const data = this.fetchFloorData(floorGroupId);
    if (!data) return false;

    if (this.activeFloorGroupId && this.activeFloorGroupId !== floorGroupId) {
      this.writePersistedState(this.activeFloorGroupId, false);
    }

    // Resolve against all known levels (gives real nextFloor-aware bounds);
    // fall back to the in-hand planes before the manager is seeded. The
    // floor's OWN footprint corners (when stored) bound the X-ray clip.
    const bounds = this.resolveLevelBounds(floorGroupId) ?? boundsFromPlanes(floorGroupId, data.floor, data.ceiling);
    this.activeSpec = defaultIsolationSpec(bounds, loadFloorFootprintCorners(floorGroupId));
    this.activeFloorGroupId = floorGroupId;
    this.resolveAllViews();
    return true;
  }

  /** Property-driven deactivation. */
  deactivate(floorGroupId: string): void {
    if (this.customMode) return;
    if (this.activeFloorGroupId !== floorGroupId) return;
    this.activeSpec = null;
    this.activeFloorGroupId = null;
    this.resolveAllViews();
  }

  // ─── Per-view resolution ───────────────────────────────────────────────

  private resolveAllViews(): void {
    for (const view of this.viewer.renderViews) this.resolveView(view);
  }

  private resolveView(view: RenderView): void {
    const fragment = this.frozenRefCount > 0 ? null : this.buildFragment(view);
    if (view.viewState) view.viewState.setFloorIsolation(fragment);
    else view.floorIsolation = fragment;
  }

  /** Resolve the strategy for a view (its override, else base), then build the clip. */
  private buildFragment(view: RenderView): IsolationClipFragment | null {
    const strategy = this.strategyForView(view);
    if (!strategy) return null;
    if (strategy.kind === 'box') return resolveStrategyToClipFragment(strategy, null, null);
    const data = this.fetchFloorData(strategy.floorGroupId);
    if (!data) return null;
    return resolveStrategyToClipFragment(strategy, data.floor, data.ceiling);
  }

  private strategyForView(view: RenderView): FloorIsolationStrategy | null {
    const spec = this.activeSpec;
    if (!spec) return null;
    const vid = this.viewModeId(view);
    if (vid != null && spec.overrides && Object.prototype.hasOwnProperty.call(spec.overrides, vid)) {
      return spec.overrides[vid];
    }
    return spec.base;
  }

  /** Current view-mode id for a view: main → global ViewManager; widget → its own. */
  private viewModeId(view: RenderView): string | null {
    if (view === this.viewer.renderViews[0]) {
      return this.viewer.personaManager?.viewManager.getCurrentView()?.id ?? null;
    }
    return view.viewState?.currentViewId.value ?? null;
  }

  private fetchFloorData(floorGroupId: string): { floor: DetectedFloorPlane; ceiling: DetectedFloorPlane | null } | null {
    return fetchFloorPlanes(floorGroupId);
  }

  private writePersistedState(floorGroupId: string, value: boolean): void {
    ensureFloorIsolationPropertyRegistered(floorGroupId);
    const property = getFloorIsolationProperty(floorGroupId);
    if (property && property.value !== value) {
      property.value = value;
    }
  }

  private handleObjectsChanged = (event: ObjectsChangedEvent): void => {
    const groups = event.floorGroupsData || [];
    this.knownFloorGroupIds.clear();
    for (const entry of groups) {
      this.knownFloorGroupIds.add(entry.groupIdentifier);
      this.ensureToggleSubscription(entry.groupIdentifier);
    }
  };

  /**
   * Subscribe to a floor's persisted toggle so the manager reacts to UI clicks
   * and DB-preloaded values alike. `subscribe` fires immediately with the
   * current value, wiring up restore-on-load. Custom mode short-circuits the
   * handler so persisted state is preserved while a tool drives isolation.
   */
  private ensureToggleSubscription(floorGroupId: string): void {
    if (this.toggleSubscriptions.has(floorGroupId)) return;
    ensureFloorIsolationPropertyRegistered(floorGroupId);
    const property = getFloorIsolationProperty(floorGroupId);
    if (!property) return;
    const sub = property.subscribe((value) => {
      if (this.customMode) return;
      if (value && !this.isActive(floorGroupId)) {
        this.activate(floorGroupId);
      } else if (!value && this.isActive(floorGroupId)) {
        this.deactivate(floorGroupId);
      }
    });
    this.toggleSubscriptions.set(floorGroupId, sub);
  }

  private handleObjectsDeleted = (event: { deletedGroupIdentifiers?: string[] }): void => {
    const deleted = event.deletedGroupIdentifiers || [];
    for (const id of deleted) {
      this.knownFloorGroupIds.delete(id);
      if (this.activeFloorGroupId === id) {
        this.activeSpec = null;
        this.activeFloorGroupId = null;
        this.resolveAllViews();
      }
      this.toggleSubscriptions.get(id)?.unsubscribe();
      this.toggleSubscriptions.delete(id);
    }
  };
}
